import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { toast } from "sonner";

// Cookie consent banner (fork 19+) — purely informational. Per user spec
// (choice A) the banner does NOT change runtime behaviour : technically
// necessary storage (JWT, guest tickets, pinned tickets, anti-double-ticket
// flags) continues to work regardless of the user's choice. This is
// legally defensible because all storage is "strictly necessary" to the
// service requested by the user (cf. Legal.jsx § 5 + CNIL recommendation
// on Art. 82 loi I&L). The banner appears once on the first visit and
// hides forever after a click on either button.

const LS_KEY = "bypassgo_cookie_consent"; // values : "accepted" | "dismissed"

export default function CookieConsentBanner() {
  // Render gate : keep `null` (= no DOM) until we've confirmed the user
  // hasn't seen the banner yet. Avoids a flash on every page load.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(LS_KEY);
      if (!v) setVisible(true);
    } catch {
      // localStorage unavailable (private mode, quota, etc.) — show the
      // banner anyway, the close handlers are wrapped in try/catch.
      setVisible(true);
    }
  }, []);

  // Allow any part of the app (typically the footer "Gérer mes cookies"
  // link) to reopen the banner on demand by dispatching a custom event.
  // Fork 19+ — lets the user revisit their consent choice after the
  // initial dismissal.
  useEffect(() => {
    const onReopen = () => setVisible(true);
    window.addEventListener("bypassgo:open-cookie-banner", onReopen);
    return () => window.removeEventListener("bypassgo:open-cookie-banner", onReopen);
  }, []);

  const close = (value) => {
    try { localStorage.setItem(LS_KEY, value); } catch { /* ignore */ }
    setVisible(false);
  };

  const onAccept = () => close("accepted");

  const onDismiss = () => {
    close("dismissed");
    // Non-blocking toast warning per user spec.
    toast.warning(
      "Certaines fonctionnalités peuvent ne pas fonctionner correctement sans cookies.",
      { duration: 6000 }
    );
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      className="fixed inset-x-0 bottom-0 z-[90] p-3 sm:p-4 pointer-events-none"
      data-testid="cookie-consent-banner"
    >
      <div className="mx-auto max-w-3xl bg-white border-2 border-ink shadow-2xl rounded-md pointer-events-auto relative">
        <div className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
          <div className="shrink-0 hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-brand/10 text-brand">
            <Cookie size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p id="cookie-banner-title" className="font-display text-sm font-black text-ink mb-1 flex items-center gap-2 sm:hidden">
              <Cookie size={16} className="text-brand shrink-0" /> Cookies &amp; stockage local
            </p>
            <p id="cookie-banner-desc" className="text-xs sm:text-sm text-ink leading-snug">
              Les cookies et le stockage local sont utilisés pour <strong>maintenir votre session</strong>, <strong>conserver vos tickets actifs</strong> et assurer le bon fonctionnement de BypassGo.
            </p>
            <p className="text-[11px] text-subtle mt-1">
              En savoir plus dans nos{" "}
              <Link
                to="/legal#cookies"
                className="underline hover:text-ink"
                data-testid="cookie-banner-legal-link"
                onClick={() => close("accepted")}
              >
                mentions légales
              </Link>
              .
            </p>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <button
              type="button"
              onClick={onDismiss}
              className="h-10 px-4 border-2 border-border text-ink hover:border-ink bg-white font-bold text-xs uppercase tracking-wider rounded-md"
              data-testid="cookie-banner-dismiss-btn"
            >
              Continuer sans accepter
            </button>
            <button
              type="button"
              onClick={onAccept}
              className="h-10 px-5 bg-brand hover:bg-brand-hover text-white font-bold text-xs uppercase tracking-wider rounded-md"
              data-testid="cookie-banner-accept-btn"
              autoFocus
            >
              Accepter
            </button>
          </div>
        </div>
        {/* Quick-dismiss "x" in the top-right corner for desktop users. */}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Fermer la bannière de cookies"
          className="absolute top-1.5 right-1.5 w-7 h-7 inline-flex items-center justify-center text-subtle hover:text-ink"
          data-testid="cookie-banner-close-btn"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
